import React, { useState, useMemo, useEffect } from "react";
import AdminLayout from "./AdminDashboardLayout";
import Button from "../../utils/Button";
import toast from "react-hot-toast";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useApiMutation } from "../../hooks/useMutation";
import Switch from "@mui/material/Switch";
import { motion } from "framer-motion";
import AddCanvasserForm, { CanvasserFormData } from "../../components/forms/AddCanvasserForm";

type Partner = {
  id: number;
  name: string;
  legalName: string;
  contact: string;
  website: string;
  orgType: string;
  partnerIdentifier: string;
  suspended?: boolean;
  teamSize: number;
  canvasserSize: number;
  activeCanvassers: number;
  totalRegistrations: number;
  enabledModules: string[];
  createdAt: string;
  updatedAt: string;
  embedEnabled?: boolean;
};

type PartnerUser = {
  id: number;
  email: string;
  fullName: string;
  role: "CANVASSER" | "PARTNER_ADMIN" | string;
  enabled: boolean;
  suspended: boolean;
  lastLoginAt?: string | null;
  lastLoginLocation?: string | null;
  phone?: string | null;
};

type PartnerUsersResponse = {
  success: boolean;
  message: string;
  data: {
    content: PartnerUser[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
};

type PartnersResponse = {
  success: boolean;
  message: string;
  data: {
    content: Partner[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
};

const Partners: React.FC = () => {
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [managePartner, setManagePartner] = useState<Partner | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [embedEnabled, setEmbedEnabled] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isEditingDomains, setIsEditingDomains] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [tempDomains, setTempDomains] = useState<string[]>([]);
  const [userAction, setUserAction] = useState<{ id: number; action: "suspend" | "unsuspend" } | null>(null);
  const [userDeleteId, setUserDeleteId] = useState<number | null>(null);
  const [isAddCanvasserModalOpen, setIsAddCanvasserModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: partnersData, isLoading } = useApiQuery<PartnersResponse>({
    queryKey: ["admin-partners", page, size],
    request: {
      url: `admin/partners?page=${page}&size=${size}`,
      method: "GET",
    },
  });

  // Embed data query for selected partner
  const { data: embedData, isLoading: embedLoading } = useApiQuery<{
    data: {
      embedId: string;
      partnerIdentifier: string;
      partnerName: string;
      enabled: boolean;
      label: string | null;
      allowedDomains: string | null;
      requestCount: number;
      lastSeenAt: string | null;
      createdAt: string;
      updatedAt: string;
      scriptLoaderUrl: string;
      iframeUrl: string;
    };
  }>({
    queryKey: ["partner-embed", selectedPartner?.partnerIdentifier],
    request: {
      url: `partner/embed/${selectedPartner?.partnerIdentifier}`,
      method: "GET",
    },
    enabled: !!selectedPartner,
    retry: false,
  });

  const { data: partnerUsers, isLoading: usersLoading, refetch: refetchPartnerUsers } = useApiQuery<PartnerUsersResponse>({
    queryKey: ["partner-users", managePartner?.id],
    request: {
      url: managePartner ? `/admin/users?partnerId=${managePartner.id}` : "",
      method: "GET",
    },
    enabled: !!managePartner && isManageModalOpen,
    retry: false,
  });

  const deactivateMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Partner deactivated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      queryClient.refetchQueries({ queryKey: ["admin-partners"] });
      setSelectedPartner((prev) => (prev ? { ...prev, suspended: true } : prev));
      setIsDeactivateModalOpen(false);
      setIsDetailsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to deactivate partner");
    },
  });

  const activateMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Partner activated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      queryClient.refetchQueries({ queryKey: ["admin-partners"] });
      setSelectedPartner((prev) => (prev ? { ...prev, suspended: false } : prev));
      setIsActivateModalOpen(false);
      setIsDetailsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to activate partner");
    },
  });

  const deleteMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Partner deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      setIsDeleteModalOpen(false);
      setIsDetailsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete partner");
    },
  });

  const embedToggleMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Partner embed status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      queryClient.invalidateQueries({ queryKey: ["partner-embed", selectedPartner?.partnerIdentifier] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update partner embed status");
    },
  });

  const updateDomainsMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Allowed domains updated successfully");
      queryClient.invalidateQueries({ queryKey: ["partner-embed", selectedPartner?.partnerIdentifier] });
      setIsEditingDomains(false);
      setTempDomains([]);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update allowed domains");
    },
  });

  const {
    refetch: runUserAction,
    isFetching: isUserActionPending,
  } = useApiQuery({
    queryKey: ["partner-user-action", userAction?.id, userAction?.action],
    request: {
      url: userAction ? `admin/users/${userAction.id}/${userAction.action}` : "",
      method: "PATCH",
    },
    enabled: false,
    retry: false,
  });

  const {
    refetch: runDeleteUser,
    isFetching: isDeleteUserPending,
  } = useApiQuery({
    queryKey: ["partner-user-delete", userDeleteId],
    request: {
      url: userDeleteId ? `admin/users/${userDeleteId}` : "",
      method: "DELETE",
    },
    enabled: false,
    retry: false,
  });

  const inviteCanvasserMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Invitation sent successfully!");
      setIsAddCanvasserModalOpen(false);
      refetchPartnerUsers();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send invitation");
    },
  });

  const totalPages = partnersData?.data?.totalPages || 1;

  const filteredPartners = useMemo(() => {
    if (!partnersData?.data?.content || !Array.isArray(partnersData.data.content)) return [];
    
    if (!searchTerm) return partnersData.data?.content;
    
    return partnersData.data.content.filter((partner) =>
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner?.contact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner?.legalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner?.website?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [partnersData, searchTerm]);

  // Update embedEnabled state when embed data changes
  useEffect(() => {
    if (embedData?.data) {
      setEmbedEnabled(embedData.data.enabled);
    } else if (!embedLoading && selectedPartner) {
      // If embed data is not found (404), set to false
      setEmbedEnabled(false);
    }
  }, [embedData, embedLoading, selectedPartner]);

  // Run canvasser activate/deactivate actions when queued
  useEffect(() => {
    if (!userAction) return;
    runUserAction()
      .then(() => {
        toast.success(userAction.action === "suspend" ? "Canvasser deactivated" : "Canvasser activated");
        refetchPartnerUsers();
      })
      .catch((error: any) => {
        toast.error(error?.message || "Unable to update canvasser status");
      })
      .finally(() => setUserAction(null));
  }, [userAction, runUserAction, refetchPartnerUsers]);

  // Run canvasser delete when queued
  useEffect(() => {
    if (userDeleteId === null) return;
    runDeleteUser()
      .then(() => {
        toast.success("Canvasser deleted");
        refetchPartnerUsers();
      })
      .catch((error: any) => {
        toast.error(error?.message || "Failed to delete canvasser");
      })
      .finally(() => setUserDeleteId(null));
  }, [userDeleteId, runDeleteUser, refetchPartnerUsers]);

  const openDetails = (partner: Partner) => {
    setSelectedPartner(partner);
    setEmbedEnabled(false); // Reset to false, will be updated by embed query
    setIsDetailsModalOpen(true);
  };

  const openManageModal = (partner: Partner) => {
    setManagePartner(partner);
    setIsManageModalOpen(true);
  };

  const closeManageModal = () => {
    setIsManageModalOpen(false);
    setManagePartner(null);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedPartner) {
      deleteMutation.mutate({
        url: `admin/partners/${selectedPartner.id}`,
        method: "DELETE",
      });
    }
  };

  const handleDeactivate = () => {
    setIsDeactivateModalOpen(true);
  };

  const handleActivate = () => {
    setIsActivateModalOpen(true);
  };

  const confirmDeactivate = () => {
    if (selectedPartner) {
      deactivateMutation.mutate({
        url: `admin/partners/${selectedPartner.id}/suspend`,
        method: "PATCH",
      });
    }
  };

  const confirmActivate = () => {
    if (selectedPartner) {
      activateMutation.mutate({
        url: `admin/partners/${selectedPartner.id}/unsuspend`,
        method: "PATCH",
      });
    }
  };

  const handleEmbedToggle = (checked: boolean) => {
    setEmbedEnabled(checked);
    if (selectedPartner) {
      embedToggleMutation.mutate({
        url: `partner/embed/${checked ? 'enable' : 'disable'}/${selectedPartner.partnerIdentifier}`,
        method: "PATCH",
      });
    }
  };

  const handleToggleUserStatus = (userId: number, suspended: boolean) => {
    setUserAction({ id: userId, action: suspended ? "unsuspend" : "suspend" });
  };

  const handleDeleteUser = (userId: number) => {
    setUserDeleteId(userId);
  };

  const handleStartEditingDomains = () => {
    const existingDomains = embedData?.data?.allowedDomains 
      ? embedData.data.allowedDomains.split(',').map(d => d.trim()).filter(d => d)
      : [];
    setTempDomains(existingDomains);
    setIsEditingDomains(true);
    setNewDomain("");
  };

  const handleAddDomain = () => {
    if (newDomain.trim() && !tempDomains.includes(newDomain.trim())) {
      setTempDomains([...tempDomains, newDomain.trim()]);
      setNewDomain("");
    }
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setTempDomains(tempDomains.filter(domain => domain !== domainToRemove));
  };

  const handleSaveDomains = () => {
    if (selectedPartner) {
      const domainsString = tempDomains.join(', ');
      updateDomainsMutation.mutate({
        url: `partner/embed/${selectedPartner.partnerIdentifier}`,
        method: "PATCH",
        data: { allowedDomains: domainsString },
      });
    }
  };

  const handleCancelEditingDomains = () => {
    setIsEditingDomains(false);
    setTempDomains([]);
    setNewDomain("");
  };

  const handleInviteCanvasser = (data: CanvasserFormData) => {
    if (!managePartner) {
      toast.error("No partner selected for canvasser invite");
      return;
    }

    inviteCanvasserMutation.mutate({
      method: "POST",
      url: `/admin/partners/${managePartner.id}/canvassers/invite`,
      data: {
        fullName: data.fullName,
        email: data.email,
      }
    } as any);
  };

  return (
    <AdminLayout role="SYSTEM_ADMIN">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Partners Management</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage and monitor all partner organizations
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search partners by name, email, organization, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Partners Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Canvassers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registrations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredPartners.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      {searchTerm ? "No partners found matching your search." : "No partners found."}
                    </td>
                  </tr>
                ) : (
                  filteredPartners?.map((partner) => (
                    <tr 
                      key={partner.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => openDetails(partner)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{partner.name}</div>
                          <div className="text-sm text-gray-500">{partner.partnerIdentifier}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{partner.orgType}</div>
                        <div className="text-sm text-gray-500">{partner.contact || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                            partner.suspended
                              ? "bg-red-100 text-red-800 border-red-200"
                              : "bg-green-100 text-green-800 border-green-200"
                          }`}
                        >
                          {partner.suspended ? "Suspended" : "Active"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {partner.activeCanvassers || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {partner.totalRegistrations || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {partner.createdAt ? format(new Date(partner.createdAt), "MMM dd, yyyy") : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetails(partner);
                            }}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            View Details
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openManageModal(partner);
                            }}
                            className="px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 font-medium shadow-sm"
                          >
                            Manage
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!searchTerm && totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                  variant="tertiary"
                  size="small"
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                  variant="tertiary"
                  size="small"
                >
                  Next
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page + 1}</span> of{" "}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setPage(index)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          index === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Partner Details Modal */}
      {isDetailsModalOpen && selectedPartner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
              onClick={() => setIsDetailsModalOpen(false)}
            />
            
            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-slate-50 to-blue-50 px-8 py-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {selectedPartner.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Partner Details</h2>
                      <p className="text-sm text-slate-600 mt-1">Manage partner information and settings</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsDetailsModalOpen(false)}
                    className="w-10 h-10 rounded-full bg-white/80 hover:bg-white text-slate-500 hover:text-slate-700 flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Basic Information Section */}
                <div className="mb-8">
                  <div className="flex items-center mb-6">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <h3 className="text-lg font-semibold text-slate-900">Basic Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Organization Name</label>
                      <p className="text-base text-slate-900 font-medium">{selectedPartner.name}</p>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Legal Name</label>
                      <p className="text-base text-slate-900 font-medium">{selectedPartner.legalName}</p>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Contact Email</label>
                      <p className="text-base text-slate-900 font-medium">{selectedPartner.contact}</p>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Website</label>
                      <a href={selectedPartner.website} target="_blank" rel="noopener noreferrer" className="text-base text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2">
                        {selectedPartner.website}
                      </a>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Organization Type</label>
                      <p className="text-base text-slate-900 font-medium">{selectedPartner.orgType}</p>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Partner ID</label>
                      <p className="text-base font-mono text-slate-900 font-medium">{selectedPartner.partnerIdentifier}</p>
                    </div>
                  </div>
                </div>

                {/* Statistics Section */}
                <div className="mb-8">
                  <div className="flex items-center mb-6">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    <h3 className="text-lg font-semibold text-slate-900">Statistics</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200/60">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Team Size</label>
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{selectedPartner.teamSize}</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border border-emerald-200/60">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Canvassers</label>
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{selectedPartner.canvasserSize}</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border border-purple-200/60">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Active</label>
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{selectedPartner.activeCanvassers}</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200/60">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-orange-700 uppercase tracking-wider">Registrations</label>
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{selectedPartner.totalRegistrations}</p>
                    </div>
                  </div>
                </div>

                {/* Modules Section */}
                <div className="mb-8">
                  <div className="flex items-center mb-6">
                    <div className="w-2 h-2 bg-violet-500 rounded-full mr-3"></div>
                    <h3 className="text-lg font-semibold text-slate-900">Enabled Modules</h3>
                  </div>
                  
                  <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-200/60">
                    <div className="flex flex-wrap gap-3">
                      {selectedPartner.enabledModules.map((module, index) => (
                        <span key={index} className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-md">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Embed Section */}
                <div className="mb-8">
                  <div className="flex items-center mb-6">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                    <h3 className="text-lg font-semibold text-slate-900">Embed Configuration</h3>
                  </div>
                  
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200/60">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <label className="text-sm font-semibold text-slate-700 mb-1">Partner Embed Status</label>
                        <p className="text-xs text-slate-500">Enable or disable embed functionality for this partner</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${embedEnabled ? 'text-emerald-600' : 'text-slate-500'}`}>
                          {embedEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                          checked={embedEnabled}
                          onChange={(e) => handleEmbedToggle(e.target.checked)}
                          disabled={embedToggleMutation.isPending}
                          color="primary"
                        />
                      </div>
                    </div>

                    {/* Embed Information */}
                    {embedLoading ? (
                      <div className="bg-white/60 rounded-lg p-6 text-center">
                        <div className="inline-flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm text-slate-600">Loading embed information...</span>
                        </div>
                      </div>
                    ) : embedData?.data ? (
                      <div className="bg-white rounded-xl p-6 space-y-6 border border-slate-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-slate-50 rounded-lg p-4">
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Embed ID</label>
                            <p className="text-sm font-mono text-slate-900 break-all">{embedData.data.embedId}</p>
                          </div>
                          
                          <div className="bg-slate-50 rounded-lg p-4">
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Request Count</label>
                            <p className="text-sm font-semibold text-slate-900">{embedData.data.requestCount.toLocaleString()}</p>
                          </div>
                          
                          <div className="bg-slate-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">Allowed Domains</label>
                              {!isEditingDomains && (
                                <button
                                  onClick={handleStartEditingDomains}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                  Add Domains
                                </button>
                              )}
                            </div>
                            
                            {isEditingDomains ? (
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {tempDomains.map((domain, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                                    >
                                      {domain}
                                      <button
                                        onClick={() => handleRemoveDomain(domain)}
                                        className="ml-2 text-blue-600 hover:text-blue-800"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </span>
                                  ))}
                                </div>
                                
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddDomain();
                                      }
                                    }}
                                    placeholder="Enter domain (e.g., https://example.com)"
                                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                  <button
                                    onClick={handleAddDomain}
                                    disabled={!newDomain.trim()}
                                    className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Add
                                  </button>
                                </div>
                                
                                <div className="flex space-x-2 pt-2">
                                  <button
                                    onClick={handleSaveDomains}
                                    disabled={updateDomainsMutation.isPending}
                                    className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {updateDomainsMutation.isPending ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={handleCancelEditingDomains}
                                    className="px-4 py-2 bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-900">
                                {embedData.data.allowedDomains || (
                                  <span className="text-slate-500 italic">No restrictions</span>
                                )}
                              </p>
                            )}
                          </div>
                          
                          <div className="bg-slate-50 rounded-lg p-4">
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Last Seen</label>
                            <p className="text-sm text-slate-900">
                              {embedData.data.lastSeenAt 
                                ? format(new Date(embedData.data.lastSeenAt), 'MMM dd, yyyy \'at\' h:mm a')
                                : <span className="text-slate-500 italic">Never</span>
                              }
                            </p>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Script Loader URL</label>
                            <div className="p-3 bg-slate-900 text-green-400 rounded-lg font-mono text-xs break-all border border-slate-300">
                              {embedData.data.scriptLoaderUrl}
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Iframe URL</label>
                            <div className="p-3 bg-slate-900 text-green-400 rounded-lg font-mono text-xs break-all border border-slate-300">
                              {embedData.data.iframeUrl}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-amber-800">No Embed URL Generated</p>
                            <p className="text-xs text-amber-600 mt-1">This partner has not generated an embed URL yet.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates Section */}
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-2 h-2 bg-slate-500 rounded-full mr-3"></div>
                    <h3 className="text-lg font-semibold text-slate-900">Timeline</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Created Date</label>
                      <p className="text-base text-slate-900 font-medium">
                        {format(new Date(selectedPartner.createdAt), 'MMMM dd, yyyy')}
                      </p>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Last Updated</label>
                      <p className="text-base text-slate-900 font-medium">
                        {format(new Date(selectedPartner.updatedAt), 'MMMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-8 py-6 border-t border-slate-200">
                <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {selectedPartner.suspended ? (
                    <button
                      onClick={handleActivate}
                      disabled={activateMutation.isPending}
                      className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {activateMutation.isPending ? (
                        <span className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Activating...</span>
                        </span>
                      ) : (
                        'Activate Partner'
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={handleDeactivate}
                      disabled={deactivateMutation.isPending}
                      className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-xl hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {deactivateMutation.isPending ? (
                        <span className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Deactivating...</span>
                        </span>
                      ) : (
                        'Deactivate Partner'
                      )}
                    </button>
                  )}
                    
                    <button
                      onClick={handleDelete}
                      disabled={deleteMutation.isPending}
                      className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-500 text-white font-medium rounded-xl hover:from-red-600 hover:to-rose-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {deleteMutation.isPending ? (
                        <span className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Deleting...</span>
                        </span>
                      ) : (
                        'Delete Partner'
                      )}
                    </button>
                  </div>
                  
                  <button
                    onClick={() => setIsDetailsModalOpen(false)}
                    className="px-6 py-2.5 bg-white text-slate-700 font-medium rounded-xl hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 border border-slate-300 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Canvassers Modal */}
      {isManageModalOpen && managePartner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm"
              onClick={closeManageModal}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Manage</p>
                  <h3 className="text-xl font-semibold text-slate-900">Canvassers – {managePartner.name}</h3>
                </div>
                <button
                  onClick={closeManageModal}
                  className="w-9 h-9 rounded-full bg-white text-slate-600 hover:bg-slate-100 flex items-center justify-center border border-slate-200 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-110px)]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Canvasser
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                      Partner Admin
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsAddCanvasserModalOpen(true)}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg font-medium shadow-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add New Canvasser
                    </motion.button>
                    
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Last Login</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {usersLoading ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                              <div className="inline-flex items-center space-x-2">
                                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                <span>Loading canvassers...</span>
                              </div>
                            </td>
                          </tr>
                        ) : partnerUsers?.data?.content?.length ? (
                          partnerUsers.data.content.map((user) => {
                            const isPartnerAdmin = user.role === "PARTNER_ADMIN";
                            return (
                              <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-slate-900">{user.fullName}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{user.email}</td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
                                      isPartnerAdmin
                                        ? "bg-purple-100 text-purple-800 border-purple-200"
                                        : "bg-emerald-100 text-emerald-800 border-emerald-200"
                                    }`}
                                  >
                                    {isPartnerAdmin ? "Partner Admin" : "Canvasser"}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
                                      user.suspended
                                        ? "bg-red-100 text-red-800 border-red-200"
                                        : "bg-green-100 text-green-800 border-green-200"
                                    }`}
                                  >
                                    {user.suspended ? "Suspended" : "Active"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  {user.lastLoginAt ? format(new Date(user.lastLoginAt), "MMM dd, yyyy h:mm a") : "Never"}
                                  {user.lastLoginLocation && (
                                    <span className="block text-xs text-slate-500">{user.lastLoginLocation}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right space-x-2">
                                  {(() => {
                                    const actionBusy = isUserActionPending && userAction?.id === user.id;
                                    const deleteBusy = isDeleteUserPending && userDeleteId === user.id;
                                    return (
                                      <>
                                        <button
                                          onClick={() => handleToggleUserStatus(user.id, user.suspended)}
                                          className={`px-3 py-1.5 rounded-md text-sm font-semibold border shadow-sm ${
                                            user.suspended
                                              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                                              : "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                                          } ${actionBusy ? "opacity-75 cursor-not-allowed" : ""}`}
                                          disabled={actionBusy}
                                        >
                                          {actionBusy
                                            ? userAction?.action === "suspend"
                                              ? "Deactivating..."
                                              : "Activating..."
                                            : user.suspended
                                            ? "Activate"
                                            : "Deactivate"}
                                        </button>
                                        <button
                                          onClick={() => handleDeleteUser(user.id)}
                                          className={`px-3 py-1.5 rounded-md text-sm font-semibold border border-red-500 text-red-600 hover:bg-red-50 ${deleteBusy ? "opacity-75 cursor-not-allowed" : ""}`}
                                          disabled={deleteBusy}
                                        >
                                          {deleteBusy ? "Deleting..." : "Delete"}
                                        </button>
                                      </>
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                              No canvassers found for this partner.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedPartner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsDeleteModalOpen(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Partner
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete <span className="font-semibold">{selectedPartner.name}</span>? This action cannot be undone and will permanently remove all associated data including canvassers and registrations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  variant="primary"
                  label={deleteMutation.isPending ? "Deleting..." : "Delete"}
                  additionalClassname="bg-red-600 text-white hover:bg-red-700 border-red-600 ml-2"
                />
                <Button
                  onClick={() => setIsDeleteModalOpen(false)}
                  variant="secondary"
                  label="Cancel"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {isDeactivateModalOpen && selectedPartner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsDeactivateModalOpen(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Deactivate Partner
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to deactivate <span className="font-semibold">{selectedPartner.name}</span>? This will temporarily suspend the partner's access and all their canvassers will be unable to perform registrations. You can reactivate the partner later if needed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  onClick={confirmDeactivate}
                  disabled={deactivateMutation.isPending}
                  variant="primary"
                  label={deactivateMutation.isPending ? "Deactivating..." : "Deactivate"}
                  additionalClassname="bg-orange-600 text-white hover:bg-orange-700 border-orange-600 ml-2"
                />
                <Button
                  onClick={() => setIsDeactivateModalOpen(false)}
                  variant="secondary"
                  label="Cancel"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activate Confirmation Modal */}
      {isActivateModalOpen && selectedPartner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsActivateModalOpen(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Activate Partner
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to activate <span className="font-semibold">{selectedPartner.name}</span>? This will restore access for the partner and their canvassers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  onClick={confirmActivate}
                  disabled={activateMutation.isPending}
                  variant="primary"
                  label={activateMutation.isPending ? "Activating..." : "Activate"}
                  additionalClassname="bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600 ml-2"
                />
                <Button
                  onClick={() => setIsActivateModalOpen(false)}
                  variant="secondary"
                  label="Cancel"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Canvasser Modal */}
      {isAddCanvasserModalOpen && (
        <AddCanvasserForm
          onSubmit={handleInviteCanvasser}
          onCancel={() => setIsAddCanvasserModalOpen(false)}
          isSubmitting={inviteCanvasserMutation.isPending}
          title={`Add New Canvasser - ${managePartner?.name}`}
        />
      )}
    </AdminLayout>
  );
};

export default Partners;
